// ============================================================
// Tenant branding for the dashboard.
//
// The dashboard is a single shared deployment, so unlike the Flutter apps
// (one build per tenant) the tenant is only known once someone logs in. The
// tenant_key they typed lives in localStorage under "tenantId" and is what we
// look up in tenants.json.
//
// Scope is deliberately narrow. One color is read from the tenant config:
//
//   primary -> the sidebar icons, and the sidebar text color on hover
//
// plus the sidebar logo. Everything else -- buttons, inputs, links, badges,
// cards, page headers, page background -- keeps the stock Doobi palette, so
// the dashboard stays visually consistent whichever tenant is signed in.
//
// It also reads `hiddenPages`: route paths the tenant does not use (e.g. LNDRY
// sells no Pro or classic subscriptions), dropped from the sidebar and the
// router in layouts/Admin.js.
//
// Resolution happens once, at module load, because assets/theme/colors.js reads
// it while building the MUI theme. That means a tenant change only takes effect
// after a full page load -- see the reload in views/auth/Login.js.
// ============================================================

import tenantsConfig from "./tenants.json";

const DEFAULT_TENANT_KEY = "main";
const DEFAULT_TITLE = "Doobi";
// palette.info.main -- what the sidebar icons were before tenant theming.
const DEFAULT_ICON = "#11cdef";

const isValidHex = (hex) => {
  const value = String(hex || "").trim().replace("#", "");
  return /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
};

/**
 * Tenant branding applies to the signed-in dashboard only -- the auth pages
 * stay Doobi-branded, since they're the Doobi product rather than the tenant's.
 * A stale tenantId from a previous session would otherwise repaint the login
 * page.
 *
 * Checking the route at module load is enough because every crossing between
 * /auth and /admin is a full page load: login does window.location.assign and
 * redirectToLogout sets window.location.href. Anything that isn't /admin (incl.
 * "/", which redirects to /auth) is treated as unbranded.
 */
const isBrandedRoute = () => {
  try {
    return window.location.pathname.startsWith("/admin");
  } catch (e) {
    return false;
  }
};

const readTenantKey = () => {
  try {
    const stored = localStorage.getItem("tenantId");
    const key = stored ? stored.trim() : "";
    return key || DEFAULT_TENANT_KEY;
  } catch (e) {
    // private browsing / storage disabled
    return DEFAULT_TENANT_KEY;
  }
};

const branded = isBrandedRoute();
const tenantKey = readTenantKey();
const tenants = tenantsConfig.tenants || {};
// Off the /admin routes we skip the lookup entirely, so branding `main` in
// tenants.json still can't bleed into the login page.
//
// An unknown tenant_key is otherwise expected (a tenant exists in the DB before
// anyone adds branding here) -- fall back to Doobi rather than breaking boot.
const tenant = (branded && tenants[tenantKey]) || {};
const colorScheme = tenant.colorScheme || {};

const primary = isValidHex(colorScheme.primary) ? colorScheme.primary : null;

// webpack's asset loader puts the URL on `.default`; jest's file transform
// returns it as a bare string. Accept either.
const assetUrl = (mod) => (mod && mod.default) || mod;

/**
 * Logo shown at the top of the sidebar. Tenant logos are bundled from
 * src/assets/tenants/<logo>; the shared Doobi yellow mark is also available as
 * `doobi_yellow_large.png` for a tenant that should use Doobi branding.
 */
const resolveLogo = () => {
  const fallback = assetUrl(require("assets/img/brand/doobi_green.png"));
  if (!tenant.logo) return fallback;
  try {
    if (tenant.logo === "doobi_yellow_large.png") {
      return assetUrl(require("assets/img/brand/doobi_yellow_large.png")) || fallback;
    }
    return assetUrl(require("assets/tenants/" + tenant.logo)) || fallback;
  } catch (e) {
    // logo declared in tenants.json but the file isn't committed yet
    return fallback;
  }
};

const hiddenPages = Array.isArray(tenant.hiddenPages) ? tenant.hiddenPages : [];

const tenantTheme = {
  key: tenantKey,
  title: tenant.title || DEFAULT_TITLE,
  logo: resolveLogo(),
  // Route paths (as in routes.js) this tenant does not use.
  hiddenPages,
  isPageHidden: (path) => hiddenPages.includes(path),
  colors: {
    // Sidebar icons, and the text color on sidebar hover.
    icon: primary || DEFAULT_ICON,
  },
};

export default tenantTheme;
